import React from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { Translate } from '@hydra/common/components';
import get from 'lodash/get';

import CrawlerTable from '@hydra/routes/crawler/components/CrawlerTable';
import Http3xxRedirectsRow from '../Http3xxRedirectsRow';

import FilterContainer from '@hydra/routes/crawler/components/FilterContainer';
import FilterCheckboxes from '@hydra/routes/crawler/components/FilterCheckboxes';
import FilterString from '@hydra/routes/crawler/components/FilterString';
import { withFilters } from '@hydra/routes/crawler/components/withFilters';

export class Http3xxRedirectsTable extends React.Component {
  static propTypes = {
    relay: PropTypes.object,
    project: PropTypes.shape({
      host: PropTypes.string,
      crawl: PropTypes.shape({
        httpStatusCategory: PropTypes.shape({
          countTypesHttp3xxRedirects: PropTypes.arrayOf(
            PropTypes.shape({
              count: PropTypes.number,
              severity: PropTypes.number,
              type: PropTypes.string
            })
          ),
          countCodesHttp3xxRedirects: PropTypes.arrayOf(
            PropTypes.shape({
              count: PropTypes.number,
              code: PropTypes.number
            })
          ),
          http3xxRedirects: PropTypes.shape({
            count: PropTypes.number,
            edges: PropTypes.arrayOf(
              PropTypes.shape({
                node: PropTypes.object
              })
            )
          })
        })
      })
    }).isRequired,
    offset: PropTypes.number,
    limit: PropTypes.number
  }

  render () {
    const typeToggles = get(this.props.project, 'crawls.edges[0].node.countTypesHttp3xxRedirects', [])
      .map(v => ({ identifier: v.type, ...v }));
    const codeToggles = get(this.props.project, 'crawls.edges[0].node.countCodesHttp3xxRedirects', [])
      .map(v => ({ identifier: v.code, label: <Translate content={`crawler_${v.code === -1 ? 'misc' : `${v.code}_issue`}`} />, ...v }));

    return (
      <>
        <FilterContainer>
          <FilterCheckboxes
            onChange={this.props.onGraphQLCheckboxInputChange('typeFilters', typeToggles)}
            checkedItems={this.props.filters.typeFilters}
            groups={typeToggles}
            name='typeFilters'
            label={<Translate content='crawler_redirect_issue' />}
          />

          <FilterCheckboxes
            onChange={this.props.onGraphQLCheckboxInputChange('codeFilters', codeToggles)}
            checkedItems={this.props.filters.codeFilters}
            groups={codeToggles}
            name='codeFilters'
            label={<Translate content='crawler_http_code' />}
          />

          <FilterString
            onChange={this.props.onGraphQLInputChange('redirectUrl')}
            value={this.props.filters.redirectUrl}
            name='redirectUrl'
            label={<Translate content='crawler_redirects_page_url' />}
          />

          <FilterString
            onChange={this.props.onGraphQLInputChange('sourceUrl')}
            value={this.props.filters.sourceUrl}
            name='sourceUrl'
            label={<Translate content='crawler_source_page_url' />}
          />
        </FilterContainer>
        <CrawlerTable {...this.props} category='httpStatusCategory' section='http3xxRedirects'>
          <CrawlerTable.TableHead>
            <Translate content='crawler_redirect_issue' size='2' />
            <Translate content='crawler_http_code' size='1' />
            <Translate content='crawler_redirects_page_url' size='5' />
            <Translate content='crawler_source_page_url' size='4' />
          </CrawlerTable.TableHead>
          <CrawlerTable.Body component={Http3xxRedirectsRow} />
        </CrawlerTable>
      </>
    );
  }
}

const withFilter3xxRedirectTable = (withFilters)(Http3xxRedirectsTable);

export default createRefetchContainer(
  withFilter3xxRedirectTable,
  {
    project: graphql`
      fragment Http3xxRedirectsTable_project on Project
      @argumentDefinitions(
        offset: {type: "Int", defaultValue: 0},
        limit: {type: "Int", defaultValue: 20},
        codeFilters: {type: "[Int]", defaultValue: []},
        typeFilters: {type: "[String]", defaultValue: []},
        redirectUrl: {type: "TextFilters", defaultValue: { operator: AND, values: [] }},
        sourceUrl: {type: "TextFilters", defaultValue: { operator: AND, values: [] }},
      ) {
        host,
        crawls (offset: 0, limit: 1) {
          edges {
            node {
              countTypesHttp3xxRedirects(sourceUrl: $sourceUrl, redirectUrl: $redirectUrl, codeFilters: $codeFilters) {
                type
                severity
                count
              }
              countCodesHttp3xxRedirects(sourceUrl: $sourceUrl, redirectUrl: $redirectUrl, typeFilters: $typeFilters) {
                code
                count
              }
              http3xxRedirects (
                offset: $offset,
                limit: $limit,
                codeFilters: $codeFilters,
                typeFilters: $typeFilters,
                sourceUrl: $sourceUrl,
                redirectUrl: $redirectUrl
              ) {
                count
                edges {
                  node {
                    ...Http3xxRedirectsRow_issue
                  }
                }
              }
            }
          }
        }
      }
    `
  },
  graphql`
      query Http3xxRedirectsTable_RefetchQuery(
        $host: String!
        $limit: Int,
        $offset: Int,
        $codeFilters: [Int],
        $typeFilters: [String],
        $sourceUrl: TextFilters,
        $redirectUrl: TextFilters) {,
        project (host: $host) {
          ...Http3xxRedirectsTable_project @arguments(
            offset: $offset,
            limit: $limit,
            codeFilters: $codeFilters,
            typeFilters: $typeFilters,
            redirectUrl: $redirectUrl,
            sourceUrl: $sourceUrl
          )
        }
      }
    `
);
